import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import EscapeGuard from '../guests/EscapeGuard';
import { DEFAULT_EVENT_WORDS } from '../eventWords';
import AddressAutocomplete from '../../shared/AddressAutocomplete';
import { BOOKING_URL_MAX, HOTEL_ADDRESS_MAX, HOTEL_NAME_MAX, RATE_NOTE_MAX, hotelForm, hotelProblem, stayRange } from './hotelBlock';

// Module 19 (BL-380) G6 - add or edit the event's hotel block. The platform popup (Create-reservation look); it holds typed text, so
// Escape never discards it (M6 rule); Cancel and the x still close. Removing the block asks first.
export default function HotelBlockPopup({ block = null, event: eventDates = null, saving = false, error = '', onSave, onRemove, onClose, words = DEFAULT_EVENT_WORDS }) {
    const [form, setForm] = useState(() => hotelForm(block, eventDates));
    const range = stayRange(eventDates);
    const [problem, setProblem] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(false);
    const set = (name) => (event) => setForm((previous) => ({ ...previous, [name]: event.target.value }));

    const submit = (event) => {
        event?.preventDefault?.();
        const message = hotelProblem(form, eventDates, block?.hotel_address);
        if (message) return setProblem(message);
        setProblem('');
        onSave(form);
    };
    const field = (id, label, control, { wide = false, hint = '' } = {}) => (
        <li className={`ia-resv-field${wide ? ' ia-resv-field--wide' : ''}`}>
            <div className="ia-resv-field__label"><label htmlFor={id}>{label}</label></div>
            {control}
            {hint && <p className="ia-resv-field__why">{hint}</p>}
        </li>
    );
    const shown = problem || error;

    return (
        <EscapeGuard>
            <Modal3D
                portal
                size="wide"
                title={block ? 'Edit hotel block' : 'Add hotel block'}
                subtitle={`The hotel your guests can book for this ${words.eventLower}. Guests see it on their page later; nothing is sent from here.`}
                testId="hotel-block-popup"
                onClose={onClose}
                data-m6-layout="modals"
                data-m6-component="EventHotelBlockPopup"
                actions={(
                    <>
                        {block && onRemove && <Button3D onClick={() => setConfirmRemove(true)} disabled={saving}>Remove block</Button3D>}
                        <Button3D onClick={onClose} disabled={saving}>Cancel</Button3D>
                        <Button3D variant="go" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save hotel block'}</Button3D>
                    </>
                )}
            >
                <form onSubmit={submit} noValidate>
                    <ul className="ia-resv-grid" aria-label="Hotel block">
                        {field('hotel-name', 'Hotel name', <input id="hotel-name" maxLength={HOTEL_NAME_MAX} value={form.hotel_name} onChange={set('hotel_name')} autoComplete="off" />, { wide: true })}
                        {field('hotel-address', 'Address', <AddressAutocomplete id="hotel-address" module="events" label="Hotel address" maxLength={HOTEL_ADDRESS_MAX} value={form.hotel_address} onChange={(hotel_address) => setForm((previous) => ({ ...previous, hotel_address }))} />, { wide: true, hint: 'Pick it from the search, or enter the full street, city, state and ZIP. A hotel name alone is not enough.' })}
                        {field('hotel-check-in', 'Check-in', <input id="hotel-check-in" type="date" min={range.min} max={range.max} value={form.check_in} onChange={set('check_in')} />)}
                        {field('hotel-check-out', 'Check-out', <input id="hotel-check-out" type="date" min={form.check_in || range.min} max={range.max} value={form.check_out} onChange={set('check_out')} />)}
                        {field('hotel-rate', 'Rate note', <input id="hotel-rate" maxLength={RATE_NOTE_MAX} value={form.rate_note} onChange={set('rate_note')} placeholder="e.g. Group rate $189 a night, breakfast included" autoComplete="off" />, { wide: true })}
                        {field('hotel-url', 'Booking link', <input id="hotel-url" type="url" inputMode="url" maxLength={BOOKING_URL_MAX} value={form.booking_url} onChange={set('booking_url')} placeholder="https://" autoComplete="off" />, { wide: true, hint: 'A secure address starting with https://. Guests open it to book at the group rate.' })}
                        {field('hotel-cutoff', 'Cut-off date', <input id="hotel-cutoff" type="date" value={form.cutoff_date} onChange={set('cutoff_date')} />, { hint: 'The last day to book at the group rate.' })}
                    </ul>
                    {shown && <p className="ia-resv-error" role="alert">{shown}</p>}
                </form>
            </Modal3D>
            {confirmRemove && (
                <Modal3D
                    portal
                    title="Remove the hotel block?"
                    subtitle={`${form.hotel_name || 'This hotel'} will no longer show on this ${words.eventLower}. Nobody is notified.`}
                    role="alertdialog"
                    size="small"
                    stripe="gold"
                    testId="hotel-remove-popup"
                    onClose={() => setConfirmRemove(false)}
                    data-m6-layout="modals"
                    data-m6-component="EventHotelRemovePopup"
                    actions={(<><Button3D onClick={() => setConfirmRemove(false)}>Keep it</Button3D><Button3D variant="go" onClick={() => { setConfirmRemove(false); onRemove(); }}>Remove block</Button3D></>)}
                >
                    <p className="text-sm">You can add a hotel block again at any time.</p>
                </Modal3D>
            )}
        </EscapeGuard>
    );
}
